import { useMemo, type ReactNode } from "react";

import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import useTranslations from "@/shared/lang/useTranslations";

import type { UserRowView } from "../hooks/useUserRowViews";
import type { UserListItem } from "../types/users-api.types";
import { OrganizationCell } from "./OrganizationCell";
import { SignInHealthCell } from "./SignInHealthCell";
import { UserAccessCell } from "./UserAccessCell";
import { UserIdentityCell } from "./UserIdentityCell";
import { UserRowActions } from "./UserRowActions";

export interface UsersTableProps {
  views: readonly UserRowView[];
  loading: boolean;
  isRefetching: boolean;
  empty: ReactNode;
  footer?: ReactNode;
  /** Hidden for a non-INTERNAL viewer: the column would be a constant, and the catalog would 403 anyway. */
  showOrganization: boolean;
  isDirectoryPending: boolean;
  /** Row id that was just mutated — gets a brief highlight so the operator sees where their action landed. */
  pingedId: string | null;
}

/**
 * The roster at `lg` and up. Below that the same rows render as cards.
 *
 * Sorting is deliberately absent: the API's order is fixed (`created_at DESC, id DESC`), so no header is
 * clickable and none shows a sort affordance. A sort control that does not sort is worse than none.
 */
export function UsersTable({
  views,
  loading,
  isRefetching,
  empty,
  footer,
  showOrganization,
  isDirectoryPending,
  pingedId,
}: UsersTableProps) {
  const { t } = useTranslations("users");

  // Memoised: six `cell` closures plus a header element rebuilt on every render, for a table whose row views
  // are themselves memoised upstream. Nothing here depends on the rows.
  const columns = useMemo<ColocaDataTableColumn<UserRowView>[]>(
    () => [
      {
        id: "person",
        header: t("col_person"),
        headerClassName: "min-w-[260px]",
        cell: (view) => <UserIdentityCell user={view.user} isCurrentUser={view.isCurrentUser} />,
      },
      ...(showOrganization
        ? [
            {
              id: "organization",
              header: t("col_organization"),
              headerClassName: "w-[220px]",
              cell: (view: UserRowView) => (
                <OrganizationCell
                  view={view.organization}
                  isDirectoryPending={isDirectoryPending}
                  isIdPending={view.isOrganizationPending}
                />
              ),
            },
          ]
        : []),
      {
        id: "access",
        header: t("col_access"),
        headerClassName: "w-[190px]",
        cell: (view) => (
          <UserAccessCell
            roles={view.roles}
            summary={view.summary}
            unavailable={view.rolesUnavailable}
            onOpen={view.onManageAccess}
          />
        ),
      },
      {
        id: "status",
        header: t("col_status"),
        headerClassName: "w-[120px]",
        cell: (view) => (
          <ColocaStatusPill variant={view.statusVariant} withDot>
            {view.statusLabel}
          </ColocaStatusPill>
        ),
      },
      {
        id: "health",
        header: t("col_account_access"),
        headerClassName: "w-[176px]",
        cell: (view) => <SignInHealthCell health={view.health} />,
      },
      {
        id: "actions",
        // Visually hidden rather than empty: a `th` with no content leaves the column unnamed in the table's
        // header row for a screen reader.
        header: <span className="sr-only">{t("col_actions")}</span>,
        headerClassName: "w-[56px]",
        cellClassName: "text-right",
        cell: (view) => <UserRowActions {...view.actions} />,
      },
    ],
    [t, showOrganization, isDirectoryPending],
  );

  return (
    // `inert`, not just `pointer-events-none`: the latter does not block the keyboard, so a keyboard user
    // could still Tab into and activate rows in a table that is visually disabled.
    <div
      className={isRefetching ? "opacity-60" : undefined}
      aria-busy={isRefetching}
      inert={isRefetching || undefined}
    >
      <ColocaDataTable<UserRowView>
        columns={columns}
        rows={views as UserRowView[]}
        rowKey={(view) => view.user.id}
        onRowClick={(view) => view.onOpenDetail()}
        loading={loading}
        skeletonRows={6}
        empty={empty}
        footer={footer}
        // The page already wraps this in a `ColocaSurface`; a second frame would double the hairline and
        // notch every corner. Sum of the declared column widths — below it the table scrolls.
        frame="none"
        minWidth={1024}
        rowClassName={(view) => rowClasses(view.user, view.isCurrentUser, view.user.id === pingedId)}
      />
    </div>
  );
}

function rowClasses(user: UserListItem, isCurrentUser: boolean, isPinged: boolean): string | undefined {
  const classes: string[] = [];
  // Dim, never tint: a table whose rows are coloured by state becomes a colour chart and the eye stops reading.
  if (user.status === "suspended") classes.push("opacity-55");
  if (isCurrentUser) classes.push("bg-primary/5");
  if (isPinged) classes.push("bg-primary/10 transition-colors duration-1000");
  return classes.length > 0 ? classes.join(" ") : undefined;
}
